import { ROUTES_SECTIONS } from '~/router/routes';

import type { RouteObject } from 'react-router-dom';
import type { NavigationContext } from '~/store/selectors/navigationSelectors';

/**
 * Checks if route belongs to at least one of activeSections
 */
const isRouteInSections = (route: RouteObject, activeSections: ROUTES_SECTIONS[]) => {
  const routeSections = route.handle?.sections;

  if (!Array.isArray(routeSections) || routeSections.length === 0) {
    return false;
  }

  return activeSections.some((s) => routeSections.includes(s));
};

/**
 * true if the route should be visible in navigation (display is absent or returned true)
 */
const isRouteVisible = (route: RouteObject, navigationContext: NavigationContext) => {
  const display = route.handle?.display;
  return !display || display(navigationContext);
};

const filterClanHubRoutesForSections = (
  params: {
    routes: RouteObject[];
    sections: ROUTES_SECTIONS[];
  },
  navigationContext: NavigationContext,
) => {
  const { routes, sections } = params;

  return routes
    .filter((route) => {
      if (sections && !isRouteInSections(route, sections)) {
        return false;
      }

      return isRouteVisible(route, navigationContext);
    })
    .sort((a, b) => {
      const aOrder = a.handle?.sectionOrder;
      const bOrder = b.handle?.sectionOrder;

      const aOrderResolved = typeof aOrder === 'function' ? aOrder(navigationContext) : aOrder;
      const bOrderResolved = typeof bOrder === 'function' ? bOrder(navigationContext) : bOrder;

      if (aOrderResolved === undefined && bOrderResolved === undefined) {
        return 0;
      }

      if (aOrderResolved === undefined) {
        return 1;
      }

      if (bOrderResolved === undefined) {
        return -1;
      }

      return aOrderResolved - bOrderResolved;
    });
};

const filterLegacyRoutesForSections = (
  params: {
    routes: RouteObject[];
    sections: ROUTES_SECTIONS[];
  },
  navigationContext: NavigationContext,
) => {
  const { routes, sections } = params;
  const isExternalClanSection = sections?.includes(ROUTES_SECTIONS.CLAN_EXTERNAL);

  return routes.filter((route) => {
    const isExternalClanRoute = isRouteInSections(route, [ROUTES_SECTIONS.CLAN_EXTERNAL]);

    return isExternalClanSection === isExternalClanRoute && isRouteVisible(route, navigationContext);
  });
};

/**
 * Filters routesConfig for a specific set of sections + display(ctx).
 */
export const filterRoutesForSections = (
  params: {
    routes: RouteObject[];
    sections: ROUTES_SECTIONS[];
  },
  navigationContext: NavigationContext,
) => {
  if (!navigationContext.isClanHubEnabled) {
    return filterLegacyRoutesForSections(params, navigationContext);
  }

  return filterClanHubRoutesForSections(params, navigationContext);
};
